import { z } from "zod";
import { BlockReason, UnblockReasonText } from "../blocklist.ts";
import { IsoDateTime, PlayerRef } from "../common.ts";
import { BlockEntryV1, OperatorFilter, PageParams, PeriodBound } from "./common.ts";

// Blocklist routes (spec player-blocklist, design D4/D8): `GET /blocks` (`explorer.read`),
// `POST /blocks` and `POST /blocks/{id}/unblock` (`blocklist.write`, CSRF token required).

/** Length bounds of a block's comment (spec player-blocklist); the domain enforces the same. */
export const BLOCK_COMMENT_MIN_LENGTH = 10;
export const BLOCK_COMMENT_MAX_LENGTH = 500;

export const BlockListQueryV1 = z
  .object({
    /** Users or Devices tab. */
    target_kind: z.enum(["player", "device"]).default("player"),
    status: z.enum(["active", "unblocked", "expired", "all"]).default("active"),
    /** Identifier search (exact `player_ref` or `device_ref`). */
    q: z.string().trim().min(1).max(128).optional(),
    reason: BlockReason.optional(),
    /** Blocks created in the period (`PeriodBound`: UTC days, inclusive, or UTC instants). */
    from: PeriodBound.optional(),
    to: PeriodBound.optional(),
    operator_id: OperatorFilter.optional(),
    ...PageParams,
  })
  .strict()
  .meta({ id: "BlockListQueryV1" });
export type BlockListQueryV1 = z.infer<typeof BlockListQueryV1>;

export const BlockListResponseV1 = z
  .object({
    total: z.int().nonnegative(),
    page: z.int().min(1),
    size: z.int().min(1),
    items: z.array(BlockEntryV1),
  })
  .strict()
  .meta({ id: "BlockListResponseV1" });
export type BlockListResponseV1 = z.infer<typeof BlockListResponseV1>;

/** Comment ≥10 characters (checked by the domain, which answers `invalid_request`). */
export const CreateBlockRequestV1 = z
  .discriminatedUnion("target_kind", [
    z
      .object({
        operator_id: z.uuid(),
        target_kind: z.literal("player"),
        target_ref: PlayerRef,
        reason: BlockReason,
        comment: z.string().min(BLOCK_COMMENT_MIN_LENGTH).max(BLOCK_COMMENT_MAX_LENGTH),
        expires_at: IsoDateTime.optional(),
      })
      .strict(),
    z
      .object({
        operator_id: z.uuid(),
        target_kind: z.literal("device"),
        target_ref: z.uuid(),
        reason: BlockReason,
        comment: z.string().min(BLOCK_COMMENT_MIN_LENGTH).max(BLOCK_COMMENT_MAX_LENGTH),
        expires_at: IsoDateTime.optional(),
      })
      .strict(),
  ])
  .meta({ id: "CreateBlockRequestV1" });
export type CreateBlockRequestV1 = z.infer<typeof CreateBlockRequestV1>;

export const UnblockRequestV1 = z
  .object({
    operator_id: z.uuid(),
    reason: UnblockReasonText,
  })
  .strict()
  .meta({ id: "UnblockRequestV1" });
export type UnblockRequestV1 = z.infer<typeof UnblockRequestV1>;
